import { type PathEmbeddedValue } from 'src/components/feedback/utils';
// ------------------------ Common Types ------------------------
export interface Data {
  value: string;
  __path: string;
}

export type TimestampedContent = {
  timestamp: Data;
  role?: Data;
  content: Data;
};

export type Transcript = TimestampedContent[];

export interface AiEnhancedValue {
  value: string;
  aiGenerated: boolean;
}

// ------------------------ Sales Summary Types ------------------------

export interface ISalesSummary {
  isBatch: boolean;
  call_metadata: {
    agent_name: AiEnhancedValue;
    dnis: string;
    duration: string;
    start_time: string;
    inbound_outbound: string;
    url: string;
  };
  loan_data: {
    borrower_name: AiEnhancedValue;
    sales_manager_name: string;
    phone_number: string;
    loan_number: string;
    fee_collected: string;
    application_gate: string;
    product_name: string;
    campaign_name: string;
    additional_loan_number?: string;
  };
  call_analysis: {
    primary_intent: PathEmbeddedValue<string>;
    primary_intent_excerpt: PathEmbeddedValue<string>;
    initial_category: PathEmbeddedValue<string>;
    initial_category_excerpt: PathEmbeddedValue<string>;
    shift_in_category: PathEmbeddedValue<string>;
    shift_in_category_excerpt: PathEmbeddedValue<string>;
    shift_in_intent: PathEmbeddedValue<string>;
    shift_in_intent_excerpt: PathEmbeddedValue<string>;
    summarize_the_conversation: PathEmbeddedValue<string>;
    sales_outcome: any; // TODO: Add type
    agent_sentiment_analysis: {
      beginning: {
        sentiment_label: PathEmbeddedValue<string>;
        excerpt: string;
      };
      middle: {
        sentiment_label: PathEmbeddedValue<string>;
        excerpt: string;
      };
      end: {
        sentiment_label: PathEmbeddedValue<string>;
        excerpt: string;
      };
    };
    customer_sentiment_analysis: {
      beginning: {
        sentiment_label: PathEmbeddedValue<string>;
        excerpt: string;
      };
      middle: {
        sentiment_label: PathEmbeddedValue<string>;
        excerpt: string;
      };
      end: {
        sentiment_label: PathEmbeddedValue<string>;
        excerpt: string;
      };
    };
    ai_insights: PathEmbeddedValue<string>[];
    transcript: Transcript;
    deviations_and_insights: any;
    different_segments: PathEmbeddedValue<string>;
    online_assistance: any;
    script_adherence: any; // TODO: Add type
    quality_results?: {
      introduction: PathEmbeddedValue<string>;
      offer_pitch: PathEmbeddedValue<string>;
      application_discovery: PathEmbeddedValue<string>;
      wrap_up: PathEmbeddedValue<string>;
    };
  };
}

// ------------------------ Servicing Summary Types ------------------------
export interface IServicingSummary {
  parentpath: string;
  loan_data: {
    // Asuming this wil be similar to ISalesSummary
    borrower_name: string;
    sales_manager_name: string;
    phone_number: string;
    loan_number: string;
    mobile: string;
    acp: string;
    repeat_caller: string;
    customer_risk: string;
    call_risk: string;
  };
  call_analysis: CallAnalysis;
  metadata: Metadata;
  transcription: Transcription[];
  __path: string;
}

export interface Transcription {
  content: Data;
  timestamp: Data;
  role: Data;
  __path: string;
}

export interface Metadata {
  agent_name: Data;
  url: string;
  __path: string;
}

export interface CallAnalysis {
  customer_intent_analysis: Customerintentanalysis;
  call_timeline: Calltimeline;
  future_payment_plans: Futurepaymentplan[];
  call_efficieny: Callefficieny;
  action_items: ActionItems;
  agent_sentiment_analysis: Agentsentimentanalysis;
  customer_issue_resolution_status: Customerissueresolutionstatus;
  inbound_outbound_call: Inboundoutboundcall;
  summary_and_insights: Summaryandinsights;
  customer_objections: Customerobjections;
  resolutions_discussed: Resolutionsdiscussed[];
  customer_sentiment_analysis: Agentsentimentanalysis;
  customer_name: Data;
  call_duration: Timeonhold;
  __path: string;
}

export interface Resolutionsdiscussed {
  resolution_discussed: Data;
  excerpt: Excerpt;
  __path: string;
}

export interface Customerobjections {
  objections: Objection[];
  __path: string;
}

export interface Objection {
  objection: Data;
  response: Data;
  excerpt: Excerpt;
  __path: string;
}

export interface Summaryandinsights {
  summary: Data[];
  insights: Data[];
  excerpt: Excerpt;
  __path: string;
}

export interface Inboundoutboundcall {
  call_type: Data;
  reasoning: Data;
  excerpt: Excerpt;
  __path: string;
}

export interface Customerissueresolutionstatus {
  resolution_status: Resolutionstatus;
  chosen_option: Data;
  explanation: Data;
  excerpt: Excerpt;
  __path: string;
}

export interface Resolutionstatus {
  value: boolean;
  __path: string;
}

export interface Agentsentimentanalysis {
  overall_sentiment: Overallsentiment;
  beginning: Overallsentiment;
  middle: Overallsentiment;
  end: Overallsentiment;
  __path: string;
}

export interface Overallsentiment {
  sentiment_label: Data;
  excerpt: Excerpt;
  __path: string;
}

export interface ActionItems {
  customer_actions: ActionItem[];
  agent_actions: ActionItem[];
  __path: string;
}

export interface ActionItem {
  action_description: Data;
  purpose: Data;
  deadline: Data;
  excerpt: Excerpt;
  follow_up_details: Data;
  __path: string;
}

export interface Callefficieny {
  time_on_hold: Timeonhold;
  number_of_holds: Timeonhold;
  transfers: Timeonhold;
  areas_for_improvement: Data;
  excerpt: Excerpt;
  __path: string;
}

export interface Timeonhold {
  value: number;
  __path: string;
}

export interface Futurepaymentplan {
  payment_plan_type: Data;
  customer_decision: Data;
  agent_guidance: Data;
  excerpt: Excerpt;
  __path: string;
}

export interface Calltimeline {
  events: Event[];
  __path: string;
}

export interface Event {
  event_description: Data;
  excerpt: Excerpt;
  __path: string;
}

export interface Customerintentanalysis {
  primary_intent: Primaryintent;
  secondary_intent: Primaryintent;
  tertiary_intent: Tertiaryintent;
  __path: string;
}

export interface Tertiaryintent {
  value: null;
  __path: string;
}

export interface Primaryintent {
  intent: Data;
  reason: Data;
  excerpt: Excerpt;
  category: Data;
  __path: string;
}

export interface Excerpt {
  excerpt: Data;
  speaker: Data;
  start_time: Data;
  end_time: Data;
  __path: string;
}

// ------------------------ Compliance Summary Types ------------------------

// export type ComplianceStatus = 'Pass' | 'Fail' | '';
interface InformationItem {
  information_type: string;
  content: string;
}

export type IComplianceBorrower = {
  borrower_number: Data;
  borrower_name: Data;
  ethnicity: DemographicInfo[];
  race: DemographicInfo[];
  sex: DemographicInfo;
};

export interface IComplianceSummary {
  call_metadata: {
    agent_name: Data;
    starttime: Data;
  };
  demographics: Demographics;
  script_adherence: IComplianceScriptAdherence;
  transcription: Transcript;
  additional_information?: InformationItem[];
  fileUrls?: any[];
}

export interface Demographics {
  demographics_overall_pass_or_fail: {
    value: string;
    __path: string;
  };
  demographics_overall_pass_or_fail_reason: {
    value: string;
    __path: string;
  };
  borrowers: IComplianceBorrower[];
}

export interface DemographicInfo {
  voice: Data;
  form_1003: Data;
  pass_or_fail: Data;
  excerpt: Data;
}

export interface ComplianceScriptAdherenceItem {
  pass_or_fail: {
    value: string;
    __path: string;
  };
  excerpt: Data;
  reason: Data;
}

export interface IComplianceScriptAdherence {
  script_adherence_overall_pass_or_fail: {
    value: string;
    __path: string;
  };
  script_adherence_overall_pass_or_fail_reason: {
    value: string;
    __path: string;
  };
  disclosure_statement: ComplianceScriptAdherenceItem;
  ethnicity: ComplianceScriptAdherenceItem[];
  race: ComplianceScriptAdherenceItem[];
  sex: ComplianceScriptAdherenceItem;
}

export enum EComplianceBorrowerFieldKey {
  NAME = 'name',
  ETHNICITY = 'ethnicity',
  RACE = 'race',
  SEX = 'sex',
}

// ------------------------ Union Type ------------------------
export type TSummary = IComplianceSummary | IServicingSummary;

// ------------------------ New Script Adherence Types ------------------------

export interface ScriptAdherenceCheckpoint {
  cp_id: PathEmbeddedValue<string>;
  cp_name: PathEmbeddedValue<string> | string;
  cp_score_type?: PathEmbeddedValue<string>;
  cp_score: PathEmbeddedValue<number | string>;
  cp_weight?: PathEmbeddedValue<number | string>;
  cp_weight_adjusted?: PathEmbeddedValue<number | string>;
  cp_weighted_score?: PathEmbeddedValue<number | string>;
  cp_reason: PathEmbeddedValue<string>;
  cp_excerpt: PathEmbeddedValue<string>;
  excerpt?: PathEmbeddedValue<string>;
  cp_summary: PathEmbeddedValue<string>;
  checkpoint_duration?: PathEmbeddedValue<string>;
  cp_duration?: PathEmbeddedValue<string>;
}

export interface OverallScriptAdherence {
  checkpoints: ScriptAdherenceCheckpoint[];
  overall_score: PathEmbeddedValue<number>;
  overall_summary: PathEmbeddedValue<string>;
  quality_results?: any;
}
